<script>
	import { simpleDateTime } from "cando-util";
	import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";

	import { store } from "$lib/utilities/store";

	import SvelteFa from "$lib/components/SvelteFa.svelte";

	export let events = {};
	export let action = null;
	export let key = "events";
	export let col;
	export let docId;
	export let existingDoc;

	let editableEvents = {};
	let previousEventsJson;
	let saveError = "";

	const copyEvents = (value) =>
		Object.fromEntries(
			Object.entries(value || {}).map(([eventId, event]) => [
				eventId,
				{
					...(event || {}),
					...(event?.source ? { source: { ...event.source } } : {}),
				},
			]),
		);

	$: eventsJson = JSON.stringify(events || {});

	$: if (eventsJson !== previousEventsJson) {
		editableEvents = copyEvents(events);
		previousEventsJson = eventsJson;
	}

	$: sortedEvents = Object.entries(editableEvents).sort(
		([eventIdA, eventA], [eventIdB, eventB]) =>
			String(eventB.createdAt || eventIdB).localeCompare(
				String(eventA.createdAt || eventIdA),
			),
	);

	const persistEvents = () => {
		saveError = "";

		if (typeof action !== "function") return;

		action(col, docId, { ...existingDoc, [key]: editableEvents }, false);
	};

	const updateEvent = (eventId, field, value) => {
		editableEvents = {
			...editableEvents,
			[eventId]: {
				...editableEvents[eventId],
				[field]: value,
			},
		};
		persistEvents();
	};

	const addEvent = () => {
		let now = new Date().toISOString();

		while (editableEvents[now]) {
			now = new Date(new Date(now).getTime() + 1).toISOString();
		}

		editableEvents = {
			...editableEvents,
			[now]: {
				createdAt: now,
				createdBy:
					$store.userProfile.email || $store.userProfile.uid || "",
				type: "General Note",
				title: "",
				visibility: "Internal",
			},
		};
		persistEvents();
	};

	const deleteEvent = (eventId) => {
		const { [eventId]: removedEvent, ...remainingEvents } = editableEvents;
		editableEvents = remainingEvents;
		persistEvents();
	};

	// user selectable only
	const eventTypeOptions = [
		"General Note",
		"Customer Contacted",
		"Law Firm Contacted",
		"Insurance Contacted",
		"Vehicle Location Updated",
		"Estimate Sent",
		"Image Report Sent",
		"EDR / CDR Sent",
		"Rental Update",
		"Repair Update",
		"Total Loss Update",
		"Final Docs Sent",
		"Case Closed",
	];
</script>

<div class="space-y-3">
	{#if action}
		<div class="flex items-center justify-between gap-3">
			<p class="text-sm font-semibold">Events</p>
			<button
				type="button"
				class="btn btn-primary btn-xs"
				on:click={addEvent}
			>
				Add Event
			</button>
		</div>
	{/if}

	{#if sortedEvents.length === 0}
		<p class="text-sm text-base-content/70">No events recorded.</p>
	{/if}

	{#each sortedEvents as [eventId, event] (eventId)}
		<div class="space-y-3 rounded-lg border border-primary bg-base-300 p-3">
			<div class="flex items-start justify-between gap-3">
				<div class="grid flex-1 gap-2 sm:grid-cols-2">
					<label class="form-control">
						<span class="label-text text-xs">Type</span>
						{#if action}
							<select
								class="select select-bordered select-sm"
								value={event.type || "General Note"}
								disabled={!eventTypeOptions.includes(
									event.type,
								)}
								on:change={(e) =>
									updateEvent(
										eventId,
										"type",
										e.currentTarget.value,
									)}
							>
								{#each [...new Set( [event.type, ...eventTypeOptions], )] as opt (opt)}
									<option value={opt}>{opt}</option>
								{/each}
							</select>
						{:else}
							<div>{event.type || "General Note"}</div>
						{/if}
					</label>
					<label class="form-control">
						<span class="label-text text-xs">Visibility</span>
						{#if action}
							<select
								class="select select-bordered select-sm"
								value={event.visibility || "Internal"}
								on:change={(e) =>
									updateEvent(
										eventId,
										"visibility",
										e.currentTarget.value,
									)}
							>
								<option value="Internal">Internal</option>
								<option value="Public">Public</option>
							</select>
						{:else}
							<div>{event.visibility || "Internal"}</div>
						{/if}
					</label>
				</div>
				{#if action}
					<button
						type="button"
						class={`btn btn-ghost btn-xs text-error ${!eventTypeOptions.includes(event.type) ? "hidden" : ""}`}
						aria-label="Delete event"
						disabled={!eventTypeOptions.includes(event.type)}
						on:click={() => deleteEvent(eventId)}
					>
						<SvelteFa icon={faTrashAlt} size="1x" />
					</button>
				{/if}
			</div>

			<div>
				<label class="form-control">
					<span class="label-text text-xs">Title</span>
					{#if action}
						<input
							class="input input-bordered input-sm w-full"
							type="text"
							value={event.title || ""}
							disabled={!eventTypeOptions.includes(event.type)}
							on:change={(e) =>
								updateEvent(
									eventId,
									"title",
									e.currentTarget.value,
								)}
						/>
					{:else}
						<div>{event.title || ""}</div>
					{/if}
				</label>
			</div>

			<div class="grid gap-2 sm:grid-cols-2">
				<div>
					<span class="label-text text-xs">Created At</span>
					<span>{simpleDateTime(event.createdAt)}</span>
				</div>
				<div>
					<span class="label-text text-xs">Created By</span>
					<span>{event.createdBy}</span>
				</div>
			</div>

			{#if event.type === "statusChanged" || "from" in event || "to" in event}
				<div class="grid gap-2 sm:grid-cols-2">
					<div>
						<span class="label-text text-xs">From</span>
						<span>{event.from}</span>
					</div>
					<div>
						<span class="label-text text-xs">To</span>
						<span>{event.to}</span>
					</div>
				</div>
			{/if}

			{#if event.source}
				<div class="rounded bg-base-200 p-2 text-xs">
					<p class="font-semibold">Source</p>
					<p>
						{event.source.type}:
						{event.source.title} ({event.source.sourceType})
					</p>
					<p>
						Path: {`${event.source.path}/${event.source.id}`}
					</p>
					{#if event.source.notes}
						<p>
							Notes: {event.source.notes}
						</p>
					{/if}
				</div>
			{/if}
		</div>
	{/each}

	{#if saveError}
		<p class="text-sm text-error">{saveError}</p>
	{/if}
</div>
